import { Link } from 'react-router'
import { Avatar } from '@carouselgg/nexus/avatar'
import { Badge } from '@carouselgg/nexus/badge'
import type { MyTeam } from '../../../api/teams'
import { teamHref } from '../destinations'
import { Panel, PanelList } from './chrome'
import { useDashboardData } from './context'
import type { WidgetManifest } from './types'
import styles from './PlayerTeamsWidget.module.css'

/**
 * The teams the viewer plays for.
 *
 * Cards, not a table: a player is looking for the one they are about to open,
 * not comparing five rosters across columns. Their role on each is the one fact
 * a card needs.
 *
 * Shares the "Your teams" heading with the operator table, which is why the two
 * are mutually exclusive — an operator who also plays gets the table, since it
 * is the strictly larger view. A registry test asserts no board ever places
 * both, because two panels with the same heading are not addressable.
 */

/** Cards before the panel stops being a summary. */
const MAX_TEAMS = 4

function PlayerTeamsWidget() {
  const { teams } = useDashboardData()

  return (
    <Panel title="Your teams" count={teams.length}>
      <PanelList>
        {teams.slice(0, MAX_TEAMS).map((team) => (
          <TeamCard key={team.id} team={team} to={teamHref(team)} />
        ))}
      </PanelList>
    </Panel>
  )
}

export function TeamCard({ team, to }: { team: MyTeam; to: string }) {
  const captain = team.role === 'captain'
  return (
    <Link to={to} className={styles.teamCard}>
      <Avatar src={team.logo_url || undefined} fallback={team.name} size="md" />
      <div className={styles.teamBody}>
        <div className={styles.teamName}>{team.name}</div>
        <div className={styles.teamGame}>{team.game_name}</div>
        <OwnershipNote team={team} />
      </div>
      <Badge variant={captain ? 'info' : 'default'} size="sm">
        {captain ? 'Captain' : 'Player'}
      </Badge>
    </Link>
  )
}

/**
 * A walk-up team the league hasn't handed to anyone yet. The card opens the
 * team's page, where the player asks.
 */
function OwnershipNote({ team }: { team: MyTeam }) {
  if (team.owner_request_pending) return <Badge variant="info" size="sm" className={styles.ownership}>Asked to own</Badge>
  if (team.owner_requestable) return <Badge variant="warning" size="sm" className={styles.ownership}>No owner yet</Badge>
  return null
}

export const playerTeamsWidget: WidgetManifest = {
  id: 'player-teams',
  title: () => 'Your teams',
  roles: ['player'],
  span: 4,
  order: 41,
  relevance: (ctx) => (ctx.multiPersona || ctx.counts.teams === 0 ? 'hide' : 'full'),
  Component: PlayerTeamsWidget,
}
